import type { DocMeta } from './db';

/**
 * Find documents for the quick switcher, across the whole library.
 *
 * The palette used to hand cmdk only the 50 most recently edited documents and
 * let cmdk filter those — so in a library of more than 50 notes, anything older
 * could not be found by typing its name at all. Matching happens here, over
 * every document, and only the best matches are rendered; that keeps the
 * palette fast at thousands of notes without making any of them unreachable.
 *
 * Every word of the query must appear in the title, an alias or the folder.
 * Title matches rank above alias and folder matches, a title that starts with
 * the query ranks highest, and ties keep the input order (most recent first).
 */
export function matchDocuments(docs: DocMeta[], query: string, limit = 50): DocMeta[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return docs.slice(0, limit);

  const whole = words.join(' ');
  const scored: { doc: DocMeta; rank: number; index: number }[] = [];

  docs.forEach((doc, index) => {
    const title = (doc.title || '').toLowerCase();
    const aliases = doc.aliases.map((a) => a.toLowerCase());
    const folder = (doc.folder ?? '').toLowerCase();
    const haystack = [title, ...aliases, folder].join('\n');
    if (!words.every((w) => haystack.includes(w))) return;

    const rank = title.startsWith(whole)
      ? 0
      : words.every((w) => title.includes(w))
        ? 1
        : aliases.some((a) => a.startsWith(whole))
          ? 2
          : 3;
    scored.push({ doc, rank, index });
  });

  return scored
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .slice(0, limit)
    .map((s) => s.doc);
}
